import { useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  GraduationCap,
  Globe,
  Sparkles,
  MapPin,
  ClipboardList,
  Plane,
  Home,
  Award,
} from "lucide-react";
import { motion } from "motion/react";
// import { ImageWithFallback } from "./figma/ImageWithFallback";
import { Link } from "react-router-dom";

const localFeatures = [
  {
    icon: <Sparkles className="w-4 h-4" />,
    title: "Up to 100% Scholarship",
    desc: "We identify and match you with full-ride scholarships across public & private institutions.",
  },
  {
    icon: <GraduationCap className="w-4 h-4" />,
    title: "300+ Partnered Universities",
    desc: "Exclusive access to Malaysia's widest network of local and overseas campus partnerships.",
  },
  {
    icon: <ClipboardList className="w-4 h-4" />,
    title: "Career Path Quiz",
    desc: "Not sure what to study? Our AI-powered quiz maps your strengths to the perfect degree.",
  },
];

const overseasFeatures = [
  {
    icon: <Award className="w-4 h-4" />,
    title: "Cheaper Than You Think",
    desc: "Study at world-class universities overseas for less than many local private colleges.",
  },
  {
    icon: <Plane className="w-4 h-4" />,
    title: "A–Z Guided Support",
    desc: "Application, visa, accommodation, pre-departure — we walk with you every single step.",
  },
  {
    icon: <Sparkles className="w-4 h-4" />,
    title: "Scholarships Available",
    desc: "Tap into government, institutional, and private scholarship pools across 40+ countries.",
  },
];

function LocalCard() {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      id="local"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="relative rounded-3xl overflow-hidden bg-white border border-slate-100 shadow-xl shadow-slate-900/5 hover:shadow-2xl hover:shadow-violet-500/10 transition-all duration-500 group flex flex-col"
    >
      {/* Top image */}
      <div className="relative h-52 overflow-hidden">
        {/* <ImageWithFallback */}
        <img
          src="https://images.unsplash.com/photo-1664273891579-22f28332f3c4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx1bml2ZXJzaXR5JTIwY2FtcHVzJTIwbW9kZXJuJTIwYnVpbGRpbmclMjBhcmNoaXRlY3R1cmV8ZW58MXx8fHwxNzc2MzI1NzE0fDA&ixlib=rb-4.1.0&q=80&w=1080"
          alt="Local university campus"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-white/60 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-br from-violet-600/20 to-transparent" />

        {/* Tag */}
        <div className="absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm shadow-sm">
          <MapPin className="w-3 h-3 text-violet-600" />
          <span
            className="text-[11px] text-violet-700"
            style={{ fontFamily: "Inter, sans-serif", fontWeight: 700 }}
          >
            STUDY LOCAL
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-8 flex flex-col">
        {/* Headline */}
        <div className="mb-6">
          <h3
            className="text-[32px] leading-tight text-slate-900 mb-2 tracking-tight"
            style={{ fontFamily: "Inter, sans-serif", fontWeight: 800 }}
          >
            Stay Close.{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #7C3AED, #8B5CF6)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Go Far.
            </span>
          </h3>
          <p
            className="text-[14px] text-slate-500 leading-relaxed"
            style={{ fontFamily: "Inter, sans-serif", fontWeight: 400 }}
          >
            World-class education on home turf. Maximize scholarships while
            building your career network right here.
          </p>
        </div>

        {/* Features */}
        <div className="flex flex-col gap-4 mb-8 flex-1">
          {localFeatures.map((feature, i) => (
            <div key={i} className="flex gap-4 items-start group/item">
              <div className="w-9 h-9 rounded-xl bg-violet-50 flex items-center justify-center flex-shrink-0 text-violet-600 group-hover/item:bg-violet-100 transition-colors">
                {feature.icon}
              </div>
              <div>
                <p
                  className="text-[14px] text-slate-900 mb-0.5"
                  style={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}
                >
                  {feature.title}
                </p>
                <p
                  className="text-[12px] text-slate-500 leading-relaxed"
                  style={{ fontFamily: "Inter, sans-serif", fontWeight: 400 }}
                >
                  {feature.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <Link
          to={"/explore-universities?type=Local"}
          className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-violet-600 to-purple-600 text-white flex items-center justify-center gap-2 shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 hover:-translate-y-0.5 transition-all duration-200"
          style={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 600,
            fontSize: "14px",
          }}
        >
          Explore Local Pathways <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Decorative blob */}
      <div className="absolute -bottom-16 -right-16 w-40 h-40 rounded-full bg-violet-100/60 blur-2xl pointer-events-none" />
    </motion.div>
  );
}

function OverseasCard() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
      id="overseas"
      className="relative rounded-3xl overflow-hidden bg-white border border-slate-100 shadow-xl shadow-slate-900/5 hover:shadow-2xl hover:shadow-orange-500/10 transition-all duration-500 group flex flex-col"
    >
      {/* Top image */}
      <div className="relative h-52 overflow-hidden">
        {/* <ImageWithFallback */}
        <img
          src="https://images.unsplash.com/photo-1752650143586-c093d519aeb7?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzdHVkZW50JTIwYWJyb2FkJTIwaW50ZXJuYXRpb25hbCUyMGNpdHklMjB0cmF2ZWwlMjBleHBsb3JlfGVufDF8fHx8MTc3NjMyNTcxNHww&ixlib=rb-4.1.0&q=80&w=1080"
          alt="Overseas adventure"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-white/60 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 to-transparent" />

        {/* Tag */}
        <div className="absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm shadow-sm">
          <Globe className="w-3 h-3 text-orange-500" />
          <span
            className="text-[11px] text-orange-600"
            style={{ fontFamily: "Inter, sans-serif", fontWeight: 700 }}
          >
            STUDY OVERSEAS
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-8 flex flex-col">
        {/* Headline */}
        <div className="mb-6">
          <h3
            className="text-[32px] leading-tight text-slate-900 mb-2 tracking-tight"
            style={{ fontFamily: "Inter, sans-serif", fontWeight: 800 }}
          >
            Go Global.{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #F97316, #EF4444)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Live Bold.
            </span>
          </h3>
          <p
            className="text-[14px] text-slate-500 leading-relaxed"
            style={{ fontFamily: "Inter, sans-serif", fontWeight: 400 }}
          >
            Study abroad affordably. We handle every detail so you can focus on
            the adventure of a lifetime.
          </p>
        </div>

        {/* Features */}
        <div className="flex flex-col gap-4 mb-8 flex-1">
          {overseasFeatures.map((feature, i) => (
            <div key={i} className="flex gap-4 items-start group/item">
              <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0 text-orange-500 group-hover/item:bg-orange-100 transition-colors">
                {feature.icon}
              </div>
              <div>
                <p
                  className="text-[14px] text-slate-900 mb-0.5"
                  style={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}
                >
                  {feature.title}
                </p>
                <p
                  className="text-[12px] text-slate-500 leading-relaxed"
                  style={{ fontFamily: "Inter, sans-serif", fontWeight: 400 }}
                >
                  {feature.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <Link
          to={"/explore-universities?type=Overseas"}
          className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-orange-500 to-red-500 text-white flex items-center justify-center gap-2 shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 hover:-translate-y-0.5 transition-all duration-200"
          style={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 600,
            fontSize: "14px",
          }}
        >
          Explore Overseas Pathways <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Decorative blob */}
      <div className="absolute -bottom-16 -right-16 w-40 h-40 rounded-full bg-orange-100/60 blur-2xl pointer-events-none" />
    </motion.div>
  );
}

export function PathwaysSection() {
  return (
    <section
      id="pathway"
      className="py-24 lg:py-32 bg-[#FAFAFA] relative overflow-hidden"
    >
      {/* Background accents */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 mb-6">
            <span
              className="text-[12px] text-slate-600 uppercase tracking-widest"
              style={{ fontFamily: "Inter, sans-serif", fontWeight: 700 }}
            >
              Choose Your Path
            </span>
          </div>
          <h2
            className="text-[40px] lg:text-[52px] text-slate-900 tracking-tight mb-4"
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 800,
              lineHeight: 1.1,
            }}
          >
            Two paths.{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #7C3AED 0%, #F97316 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Infinite possibilities.
            </span>
          </h2>
          <p
            className="text-[16px] text-slate-500 max-w-xl mx-auto"
            style={{ fontFamily: "Inter, sans-serif", fontWeight: 400 }}
          >
            Whether you're staying local or going global, our consultants tailor
            every step to your ambitions, budget, and dreams.
          </p>
        </motion.div>

        {/* Cards grid */}
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          <LocalCard />
          <OverseasCard />
        </div>
      </div>
    </section>
  );
}
